import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, RSD, imgURL, fmtDateTime } from "../../lib/api";
import { useAuth, startGoogleLogin } from "../../context/AuthContext";
import { useOrderSocket } from "../../hooks/useOrderSocket";
import { BagLogo, BagLogoTile } from "../../components/Logo";
import { User, ChevronRight, Search, Store, X } from "lucide-react";
import { toast } from "sonner";
import EmailVerify from "./EmailVerify";
import { NotifyButton } from "../../components/NotifyButton";
import { useI18n } from "../../context/I18nContext";
import { useCity } from "../../context/CityContext";
import { CitySelector } from "../../components/CitySelector";
import { RestaurantOperationalInfo } from "../../components/RestaurantOperationalInfo";
import { useInstallMode, openInstallPrompt } from "../../lib/pwaInstall";
import { Download } from "lucide-react";
import { CompactRemaining } from "../../components/OrderCountdown";
import { useServerTick } from "../../lib/serverClock";
import { useCart } from "../../context/CartContext";
import { planReorder } from "../../lib/reorder";
import { track, normalizeQuery, impressionObserver } from "../../lib/analytics";

function ActiveOrdersList({ orders, Card }) {
  const now = useServerTick(orders.length > 0);
  return <>{orders.map((o) => Card(o, now))}</>;
}

function PushToggle() {
  return <NotifyButton variant="row" />;
}

function StatusBadge({ r }) {
  const { t } = useI18n();
  const cls = r.open_now ? (r.accepting_orders ? "bg-brand-green" : "bg-brand-orange") : "bg-slate-500";
  const label = !r.open_now ? t("badge.closed", "Zatvoreno") : r.accepting_orders ? t("badge.open", "Otvoreno") : t("badge.paused", "Pauza");
  return <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full text-white ${cls}`}>{label}</span>;
}

function SectionLabel({ children }) {
  return <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">{children}</p>;
}

function RestaurantCard({ r, onOpen, reorderOrderId, onQuickReorder, reordering, cardRef }) {
  const { locale, t } = useI18n();
  const go = () => {
    track("restaurant_card_click", { restaurant_id: r.id, city_id: r.city_id,
                                     from_search: sessionStorage.getItem("ponesi_from_search") === "1" });
    onOpen(r);
  };
  return (
    <div role="link" tabIndex={0} onClick={go} ref={cardRef}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
      data-testid={`restaurant-card-${r.slug}`}
      className="block bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] animate-slide-up cursor-pointer transition-all hover:shadow-[0_12px_40px_rgb(0,0,0,0.10)] active:scale-[0.99]">
      <div className="relative h-40">
        {r.cover_image ? <img src={imgURL(r.cover_image)} alt={r.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-brand-orange to-brand-red" />}
        <div className="absolute top-3 right-3"><StatusBadge r={r} /></div>
        {r.store_promo_active ? (
          <span data-testid={`store-promo-tag-${r.slug}`}
            className="absolute top-3 left-3 text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full bg-brand-red text-white shadow">
            -{r.store_promo_percent}% {t("badge.store_promo", "na sve")}
          </span>
        ) : r.happy_hour_active && (
          <span data-testid={`hh-tag-${r.slug}`}
            className="absolute top-3 left-3 text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full bg-amber-400 text-slate-900 shadow">
            {t("badge.happy_hour", "Happy Hour")}
          </span>
        )}
        {r.is_hiring && (
          <span data-testid={`hiring-tag-${r.slug}`}
            className="absolute bottom-3 left-3 text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full bg-blue-600 text-white shadow">
            {t("badge.hiring", "Zapošljavamo")}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start gap-2">
          <h3 className="font-display font-black text-xl flex-1 min-w-0">{r.name}</h3>
          {reorderOrderId && (
            <button data-testid={`quick-reorder-${r.slug}`} type="button" disabled={reordering}
              title={t("qr.aria", "Poruči kao prošli put")} aria-label={t("qr.aria", "Poruči kao prošli put")}
              onClick={(e) => { e.stopPropagation(); onQuickReorder(r, reorderOrderId); }}
              className="-mt-1 -mr-1 w-11 h-11 shrink-0 flex items-center justify-center rounded-full hover:bg-brand-red/5 active:scale-95 transition-colors disabled:opacity-50">
              {reordering
                ? <span className="w-4 h-4 rounded-full border-2 border-brand-red/30 border-t-brand-red animate-spin" />
                : <img src="/reorder-heart-96.png" srcSet="/reorder-heart-96.png 1x, /reorder-heart-192.png 2x" alt="" aria-hidden="true" className="w-7 h-7 object-contain select-none pointer-events-none" />}
            </button>
          )}
        </div>
        {r.category && <p className="text-sm text-slate-500 mt-0.5">{r.category}</p>}
        {r.description && <p className="text-sm text-slate-400 mt-1 line-clamp-2">{r.description}</p>}
        <RestaurantOperationalInfo restaurant={r} locale={locale} t={t} className="mt-3" />
      </div>
    </div>
  );
}

export default function PlatformHome() {
  const navigate = useNavigate();
  const { user, logout, checkAuth, setUser } = useAuth();
  const { t, localePath, locale, switchLocale } = useI18n();
  const installMode = useInstallMode();
  const [restaurants, setRestaurants] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [emailVerifyOpen, setEmailVerifyOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  const { selectedCityId } = useCity();

  useEffect(() => { if (accountOpen && user) setPhoneInput(user.phone || ""); }, [accountOpen, user]);
  // Lock the homepage behind the profile/orders sheets and restore the exact scroll position on close.
  useEffect(() => {
    if (!accountOpen && !ordersOpen) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };
    body.style.position = "fixed"; body.style.top = `-${scrollY}px`; body.style.width = "100%"; body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position; body.style.top = prev.top; body.style.width = prev.width; body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [accountOpen, ordersOpen]);
  const savePhone = async () => {
    setSavingPhone(true);
    try {
      const { data } = await api.patch("/auth/phone", { phone: phoneInput });
      setUser((u) => (u ? { ...u, phone: data.phone } : u));
      setPhoneInput(data.phone);
      toast.success(t("acct.phone_saved", "Broj telefona je sačuvan"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("acct.phone_error", "Greška pri čuvanju broja"));
    } finally { setSavingPhone(false); }
  };

  const load = async () => { try { const { data } = await api.get("/restaurants"); setRestaurants(data); } catch {} };
  const loadMine = async () => { if (!user) return; try { const { data } = await api.get("/orders/mine"); setMyOrders(data); } catch {} };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadMine(); }, [user]);
  // After login, if the account still needs Ponesi email verification, prompt once (auto-sends the code).
  useEffect(() => {
    if (user?.email_verification_required && !sessionStorage.getItem("ponesi_email_prompted")) {
      sessionStorage.setItem("ponesi_email_prompted", "1");
      setEmailVerifyOpen(true);
    }
  }, [user]);
  useOrderSocket((msg) => { if (msg.type === "settings_update") load(); if (msg.type === "order_update" && user && msg.order?.user_id === user.user_id) loadMine(); }, { userId: user?.user_id });

  const activeOrders = myOrders.filter((o) => !["completed", "cancelled", "rejected"].includes(o.status));

  const listView = useMemo(() => {
    if (selectedCityId) return restaurants.filter((r) => r.city_id === selectedCityId);
    return restaurants;
  }, [restaurants, selectedCityId]);

  const openRestaurant = (r) => navigate(localePath(`/restaurant/${r.slug}`));

  // Card impressions: one per restaurant per session, only after it is genuinely on screen.
  const cardIoRef = useRef(null);
  const cardMetaRef = useRef(new Map());
  useEffect(() => {
    cardIoRef.current = impressionObserver("restaurant_card_impression", (el) => {
      const r = cardMetaRef.current.get(el);
      return r ? { restaurant_id: r.id, city_id: r.city_id, dedupe_key: r.id } : {};
    });
    return () => cardIoRef.current?.disconnect();
  }, []);
  const registerCard = (el, r) => {
    if (!el || !cardIoRef.current) return;
    cardMetaRef.current.set(el, r);
    cardIoRef.current.observe(el);
  };

  // ---- Icon-only quick reorder (latest COMPLETED order for that restaurant) ----
  const { replaceAll, items: cartItems, restaurant: cartRest } = useCart();
  const [reorderMap, setReorderMap] = useState({});
  const [reorderingId, setReorderingId] = useState(null);
  const [replaceAsk, setReplaceAsk] = useState(null);

  const loadReorderable = async () => {
    if (!user) { setReorderMap({}); return; }
    try {
      const { data } = await api.get("/orders/reorderable");
      setReorderMap(Object.fromEntries((data || []).map((x) => [x.restaurant_id, x.order_id])));
    } catch { setReorderMap({}); }
  };
  useEffect(() => { loadReorderable(); }, [user]);

  // Visibility of the quick-reorder affordance, once per restaurant per session.
  useEffect(() => {
    Object.entries(reorderMap).forEach(([restaurantId, orderId]) => {
      if (orderId) track("quick_reorder_visible", { restaurant_id: restaurantId, dedupe_key: restaurantId });
    });
  }, [reorderMap]);

  const runQuickReorder = async (r, orderId) => {
    setReorderingId(r.id);
    try {
      const [{ data: order }, { data: menu }] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get(`/restaurants/${r.slug}/menu`),
      ]);
      const { plan, skipped, optionIssues } = planReorder(order, menu.products);
      if (plan.length === 0) {
        toast.error(t("qr.nothing", "Nijedna stavka iz prethodne porudžbine više nije dostupna."));
        return;
      }
      const rest = { slug: r.slug, name: r.name, id: r.id, payment_method: r.payment_method };
      replaceAll(plan, rest, {
        pickup_type: order.pickup_type === "scheduled" ? "scheduled" : "asap",
        payment_method: order.payment_method === "card" ? "card" : "cash",
      });
      if (skipped > 0) toast.warning(t("qr.some_unavailable", "Neke stavke iz prethodne porudžbine više nisu dostupne."));
      if (optionIssues > 0) toast.warning(t("qr.option_unavailable", "Neke opcije više nisu dostupne. Proverite korpu i izaberite nove."));
      track("quick_reorder_cart_created", { restaurant_id: r.id, items: plan.length, skipped });
      try { sessionStorage.setItem("ponesi_quick_reorder_rid", r.id); } catch {}
      navigate(localePath(`/restaurant/${r.slug}?cart=1`));
    } catch {
      toast.error(t("common.error", "Greška"));
    } finally { setReorderingId(null); }
  };

  const quickReorder = (r, orderId) => {
    track("quick_reorder_clicked", { restaurant_id: r.id });
    if (cartItems.length > 0) { setReplaceAsk({ r, orderId, sameRest: cartRest?.slug === r.slug }); return; }
    runQuickReorder(r, orderId);
  };

  const [searchQuery, setSearchQuery] = useState("");
  const q = searchQuery.trim().toLowerCase();
  const filteredList = useMemo(() => {
    if (!q) return listView;
    return listView.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.category || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q));
  }, [listView, q]);
  // Debounced search tracking: the query term itself is the dedupe key, so typing one
  // search reports one event; empty-result searches are reported separately.
  useEffect(() => {
    if (q.length < 2) return;
    const id = setTimeout(() => {
      track(filteredList.length ? "search_performed" : "search_no_results",
            { dedupe_key: q, normalized_query: normalizeQuery(q),
              query_length: q.length, results: filteredList.length });
      try { sessionStorage.setItem("ponesi_from_search", "1"); } catch {}
    }, 900);
    return () => clearTimeout(id);
  }, [q, filteredList.length]);

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="max-w-md mx-auto bg-[#F9F9F7] min-h-screen shadow-2xl relative pb-16">
        <header className="bg-brand-red text-white rounded-b-[2rem] px-5 pt-6 pb-7 shadow-lg">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <BagLogoTile size={72} data-testid="header-logo" />
              <div className="min-w-0">
                <h1 className="font-display font-black text-3xl leading-none truncate">Ponesi</h1>
                <p className="text-white/80 text-xs mt-1 whitespace-nowrap">{t("home.tagline", "Poruči. Ponesi. Uživaj.")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid="account-btn" aria-label={t("acct.open_profile", "Otvori profil")}
                onClick={() => (user ? setAccountOpen(true) : startGoogleLogin("/"))}
                className="bg-white/15 hover:bg-white/25 rounded-full w-12 h-12 flex items-center justify-center overflow-hidden transition-colors shrink-0 active:scale-95">
                {user?.picture ? <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" /> : <User size={26} />}
              </button>
            </div>
          </div>
          <div className="mt-5 bg-white rounded-2xl flex items-center gap-2 px-4 py-3" data-testid="search-bar">
            <Search size={18} className="text-slate-400 flex-shrink-0" />
            <input data-testid="search-input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t("home.search", "Pretraži restorane")} placeholder={t("home.search", "Pretraži restorane")}
              className="bg-transparent outline-none text-sm flex-1 text-slate-700 placeholder:text-slate-400" />
            {searchQuery && (
              <button data-testid="search-clear" aria-label={t("home.clear_search", "Obriši pretragu")} onClick={() => setSearchQuery("")}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0"><X size={16} /></button>
            )}
          </div>
        </header>

        {user && activeOrders.length > 0 && (
          <button data-testid="active-order-banner" onClick={() => navigate(localePath(`/order/${activeOrders[0].id}`))}
            className="w-[calc(100%-2.5rem)] mx-5 mt-4 flex items-center justify-between bg-brand-green text-white rounded-2xl px-4 py-3 animate-slide-up">
            <span className="font-semibold text-sm">{t("home.active_order", "Aktivna porudžbina")} #{activeOrders[0].order_number} · {activeOrders[0].restaurant_name}</span>
            <ChevronRight size={18} />
          </button>
        )}

        {user?.email_verification_required && (
          <button data-testid="verify-email-banner" onClick={() => setEmailVerifyOpen(true)}
            className="w-[calc(100%-2.5rem)] mx-5 mt-4 flex items-center justify-between bg-brand-orange text-white rounded-2xl px-4 py-3 animate-slide-up">
            <span className="font-semibold text-sm">{t("home.verify_email_banner", "Verifikuj email da bi mogao da poručiš")}</span>
            <ChevronRight size={18} />
          </button>
        )}

        <main className="px-5 mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Store size={20} className="text-brand-red" />
            <h2 className="font-display font-black text-2xl">{t("home.restaurants", "Restorani")}</h2>
          </div>

          <div className="space-y-5">
            {filteredList.length === 0 && (
              <p className="text-slate-400 text-center py-10" data-testid="no-results">
                {q ? t("home.no_results", "Nema rezultata.") : t("home.none_available", "Trenutno nema dostupnih restorana.")}
              </p>
            )}
            {filteredList.map((r) => (
              <RestaurantCard key={r.id} r={r} onOpen={openRestaurant}
                cardRef={(el) => registerCard(el, r)}
                reorderOrderId={reorderMap[r.id]} onQuickReorder={quickReorder} reordering={reorderingId === r.id} />
            ))}
          </div>
        </main>

        {replaceAsk && (
          <div className="fixed inset-0 z-[60] max-w-md mx-auto flex items-end" data-testid="quick-reorder-replace">
            <div className="absolute inset-0 bg-black/50" onClick={() => setReplaceAsk(null)} />
            <div className="relative w-full bg-white rounded-t-3xl p-6 animate-slide-up">
              <p className="text-slate-700">
                {replaceAsk.sameRest
                  ? t("qr.replace_same", "Već imate proizvode u korpi. Da li želite da ih zamenite prethodnom porudžbinom?")
                  : t("qr.replace_other", "Vaša korpa sadrži proizvode iz drugog restorana. Da li želite da je zamenite prethodnom porudžbinom?")}
              </p>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <button data-testid="qr-replace-cancel" onClick={() => setReplaceAsk(null)} className="rounded-full py-3 bg-slate-100 font-bold">{t("common.cancel", "Otkaži")}</button>
                <button data-testid="qr-replace-confirm" onClick={() => { const a = replaceAsk; setReplaceAsk(null); runQuickReorder(a.r, a.orderId); }}
                  className="rounded-full py-3 bg-brand-red text-white font-bold">{t("qr.replace_confirm", "Zameni korpu")}</button>
              </div>
            </div>
          </div>
        )}

        <footer className="px-5 mt-8 pb-8 text-center" data-testid="app-footer">
          <div className="flex items-center justify-center gap-1.5 text-slate-400"><BagLogo size={22} /><span className="font-display font-bold text-slate-500">Ponesi</span></div>
          <p className="text-xs text-slate-400 mt-1">{t("home.tagline", "Poruči. Ponesi. Uživaj.")}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-400">
            <a href="/kontakt" data-testid="footer-contact" className="hover:text-brand-red">{t("acct.contact", "Kontakt")}</a><span>·</span>
            <a href="/b2b" data-testid="footer-b2b" className="hover:text-brand-red">Ponesi B2B</a><span>·</span>
            <a href={locale === "en" ? "/en/privacy" : "/politika-privatnosti"} data-testid="footer-privacy" className="hover:text-brand-red">{t("acct.privacy", "Politika privatnosti")}</a><span>·</span>
            <a href={locale === "en" ? "/en/terms" : "/uslovi-koriscenja"} data-testid="footer-terms" className="hover:text-brand-red">{t("acct.terms", "Uslovi korišćenja")}</a>
          </div>
        </footer>

        {accountOpen && user && (
          <div className="fixed inset-0 z-50 max-w-md mx-auto" data-testid="account-sheet">
            <div className="absolute inset-0 bg-black/40" onClick={() => setAccountOpen(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl flex flex-col max-h-[88vh] animate-slide-up">
              <div className="flex items-center justify-between p-6 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-3 min-w-0">
                  {user.picture && <img src={user.picture} className="w-12 h-12 rounded-full shrink-0" alt="" />}
                  <div className="min-w-0"><p className="font-display font-bold text-lg truncate">{user.name}</p><p className="text-sm text-muted-foreground truncate">{user.email}</p></div>
                </div>
                <button data-testid="account-close" aria-label={t("common.close", "Zatvori")} onClick={() => setAccountOpen(false)} className="bg-slate-100 rounded-full p-2 shrink-0"><X size={18} /></button>
              </div>

              <div className="overflow-y-auto overscroll-contain p-6 pt-4">
                <section data-testid="account-section">
                  <SectionLabel>{t("acct.account_section", "Nalog")}</SectionLabel>
                  <div className="bg-slate-100 rounded-2xl px-4 py-3" data-testid="phone-setting">
                    <label className="font-semibold text-sm">{t("acct.phone", "Broj telefona")}</label>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("acct.phone_hint", "Restoran vas kontaktira na ovaj broj za nove porudžbine.")}</p>
                    <div className="flex gap-2 mt-2">
                      <input data-testid="phone-input" type="tel" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)}
                        placeholder="+381 6X XXX XXXX"
                        className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white outline-none focus:border-brand-red" />
                      <button data-testid="phone-save-btn" onClick={savePhone} disabled={savingPhone}
                        className="rounded-xl px-4 py-2 text-sm font-bold bg-brand-red text-white disabled:opacity-50">{t("acct.save", "Sačuvaj")}</button>
                    </div>
                  </div>
                  <button data-testid="my-orders-btn" onClick={() => { setAccountOpen(false); setOrdersOpen(true); }} className="mt-2 w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">{t("acct.my_orders", "Moje porudžbine")} <ChevronRight size={18} /></button>
                </section>

                <section data-testid="settings-section" className="mt-5">
                  <SectionLabel>{t("acct.settings_section", "Podešavanja")}</SectionLabel>
                  <CitySelector variant="row" />
                  <div className="mt-2 bg-slate-100 rounded-2xl px-4 py-3" data-testid="language-setting">
                    <p className="font-semibold mb-2">{t("acct.language", "Jezik")}</p>
                    <div className="flex gap-2">
                      <button data-testid="lang-sr" onClick={() => switchLocale("sr")}
                        className={`flex-1 rounded-xl py-2 text-sm font-bold ${locale === "sr" ? "bg-brand-red text-white" : "bg-white text-slate-600"}`}>{t("acct.lang_sr", "Srpski")}</button>
                      <button data-testid="lang-en" onClick={() => switchLocale("en")}
                        className={`flex-1 rounded-xl py-2 text-sm font-bold ${locale === "en" ? "bg-brand-red text-white" : "bg-white text-slate-600"}`}>{t("acct.lang_en", "English")}</button>
                    </div>
                  </div>
                  <PushToggle />
                  {installMode !== "installed" && installMode !== "none" && (
                    <button data-testid="install-app-btn" onClick={() => { setAccountOpen(false); openInstallPrompt(); }}
                      className="mt-2 w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">
                      <span className="flex items-center gap-2"><Download size={18} className="text-brand-red" /> {t("acct.install", "Instaliraj Ponesi")}</span> <ChevronRight size={18} />
                    </button>
                  )}
                </section>

                {(user.memberships?.length > 0 || user.role === "admin") && (
                  <section data-testid="access-section" className="mt-5">
                    <SectionLabel>{t("acct.access_section", "Pristup")}</SectionLabel>
                    {(user.memberships?.length > 0 || user.role === "admin") && <a href="/restaurant" data-testid="restaurant-panel-link" className="w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">{t("acct.restaurant_panel", "Restoran panel")} <ChevronRight size={18} /></a>}
                    {user.role === "admin" && <a href="/admin" data-testid="admin-panel-link" className="mt-2 w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">{t("acct.admin_panel", "Admin panel")} <ChevronRight size={18} /></a>}
                  </section>
                )}

                <button data-testid="logout-btn" onClick={async () => { await logout(); setAccountOpen(false); toast.success(t("acct.logout_toast", "Odjavljeni ste")); }} className="mt-5 w-full bg-brand-red/10 text-brand-red rounded-2xl px-4 py-3.5 font-bold">{t("acct.logout", "Odjava")}</button>
              </div>
            </div>
          </div>
        )}

        {ordersOpen && (
          <div className="fixed inset-0 z-50 max-w-md mx-auto" data-testid="orders-sheet">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOrdersOpen(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto animate-slide-up">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-black text-xl">{t("acct.my_orders", "Moje porudžbine")}</h3>
                <button onClick={() => setOrdersOpen(false)}><X size={20} /></button>
              </div>
              {myOrders.length === 0 && <p className="text-muted-foreground">{t("acct.no_orders", "Još nemate porudžbina.")}</p>}
              {(() => {
                const active = myOrders.filter((o) => ["new", "time_change_pending", "accepted", "preparing", "ready"].includes(o.status));
                const history = myOrders.filter((o) => ["completed", "cancelled", "rejected"].includes(o.status)).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                const label = { completed: t("order.picked_up", "Preuzeto"), cancelled: t("order.cancelled", "Otkazano"), rejected: t("order.rejected", "Odbijeno"), ready: t("status.ready", "Spremno"), preparing: t("status.preparing", "Priprema se"), new: t("status.new", "Poslato"), accepted: t("status.accepted", "Prihvaćeno"), time_change_pending: t("status.new", "Poslato") };
                const Card = (o, now) => (
                  <button key={o.id} data-testid={`order-card-${o.order_number}`} onClick={() => { setOrdersOpen(false); navigate(localePath(`/order/${o.id}`)); }} className="w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3">
                    <div className="text-left"><p className="font-bold">#{o.order_number} · {o.restaurant_name}</p><p className="text-xs text-muted-foreground">{fmtDateTime(o.created_at, locale)}</p><p className="text-xs text-muted-foreground">{o.items.length} {t("acct.items", "stavki")} · {RSD(o.total)}</p></div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className={`text-xs font-bold uppercase ${o.status === "completed" ? "text-brand-green" : ["cancelled", "rejected"].includes(o.status) ? "text-slate-400" : "text-brand-red"}`}>{label[o.status] || o.status}</span>
                      {now !== undefined && <CompactRemaining order={o} now={now} />}
                    </div>
                  </button>
                );
                return (
                  <div className="space-y-5">
                    {active.length > 0 && (
                      <div data-testid="active-orders-section">
                        <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">{t("acct.active_orders", "Aktivne porudžbine")}</div>
                        <div className="space-y-2"><ActiveOrdersList orders={active} Card={Card} /></div>
                      </div>
                    )}
                    {history.length > 0 && (
                      <div data-testid="history-orders-section">
                        <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">{t("acct.order_history", "Istorija porudžbina")}</div>
                        <div className="space-y-2">{history.map((o) => Card(o))}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <EmailVerify open={emailVerifyOpen} email={user?.email} onClose={() => setEmailVerifyOpen(false)}
          onVerified={() => { setEmailVerifyOpen(false); checkAuth(); }} />
      </div>
    </div>
  );
}
